<template>
  <div>
    <v-row no-gutters>
      <span class="textLight--text font-weight-medium text-h5 text-left">
        {{ $t("user.profile.share") }}
      </span>
    </v-row>

    <v-row>
      <v-col>
        <v-card flat rounded class="py-6 py-xl-8 px-6">
          <v-row justify="center">
            <v-col class="d-flex justify-center">
              <div
                ref="qrCodeSvg"
                class="white pa-3 pb-2 rounded-1"
                v-html="
                  qrCodeSvg($vuetify.breakpoint.lgAndUp ? 260 : 180, true)
                "
                :style="{
                  width: $vuetify.breakpoint.lgAndUp ? '260px' : '180px',
                }"
              ></div>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col>
              <v-btn
                outlined
                block
                class="subtitle-2 textLight--text text-capitalize rounded-2"
                depressed
                :loading="downloadQrCodeLoading"
                @click="downloadQrCode"
              >
                <v-icon left small> mdi-download-outline </v-icon>
                {{ $t("user.profile.download") }}
              </v-btn>
            </v-col>

            <v-col>
              <v-speed-dial v-model="shareFab">
                <template v-slot:activator>
                  <v-btn
                    v-model="shareFab"
                    outlined
                    block
                    class="subtitle-2 textLight--text text-capitalize rounded-2"
                    depressed
                  >
                    <v-icon :left="!shareFab" small>
                      {{ shareFab ? "mdi-close" : "mdi-share-outline" }}
                    </v-icon>

                    <span v-if="!shareFab">{{ $t("user.profile.share") }}</span>
                  </v-btn>
                </template>

                <v-card class="pa-2 text-center">
                  <share-it
                    icons
                    outline
                    round
                    :text="$t('user.profile.checkoutEmployeeProfile')"
                    :url="shareUrl"
                    :targets="shareTargets"
                  />
                </v-card>
              </v-speed-dial>
            </v-col>
          </v-row>

          <v-row class="mt-6 px-3">
            <span
              class="
                subtitle-1
                font-weight-light
                textLight--text
                text-start text-ellipse-1
              "
              style="max-width: 90%"
            >
              {{ shareUrl }}
            </span>

            <v-spacer />

            <v-progress-circular
              v-if="copyingShareUrl"
              indeterminate
              size="20"
            />

            <v-icon v-else spin small @click="copyToClipBoard(shareUrl)">
              mdi-content-copy
            </v-icon>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col class="pt-0">
        <v-btn
          v-if="userProfile"
          block
          class="subtitle-1 textLight--text text-capitalize rounded-2"
          depressed
          color="button"
          large
          :loading="downloadProfileLoading"
          @click="downloadProfileCardAsPdf"
        >
          <v-icon left small> mdi-download-outline </v-icon>

          {{ $t("user.profile.downloadEmployeeCard") }}
        </v-btn>

        <v-btn
          v-if="userProfile"
          block
          class="subtitle-1 textLight--text text-capitalize rounded-2 mt-3"
          depressed
          color="button"
          large
          :loading="downloadCertificatesLoading"
          @click="downloadCertificatesAsPdf"
        >
          <v-icon left small> mdi-download-outline </v-icon>

          {{ $t("user.profile.downloadEmployeeCertificates") }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import QrCodeHelper from "qrcode-svg";

import { bulkDownloadProfileCards } from "@/app/user-module/helpers/profileCardHelper";
import { bulkDownloadTrainingCertificates } from "@/app/user-module/helpers/certificateHelper";

export default {
  name: "ProfileQrCode",
  props: {
    userProfile: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      shareFab: false,
      copyingShareUrl: false,
      subscriberInfo: null,
      downloadProfileLoading: false,
      downloadCertificatesLoading: false,
      downloadQrCodeLoading: false,
    };
  },
  computed: {
    shareUrl() {
      const domainUrl = `${process.env.VUE_APP_EMPLOYEE_URL}/${
        this.$i18n.locale || "en"
      }`;

      return `${domainUrl}/public-profile/${this.userProfile?._id}`;
    },
    shareTargets() {
      return ["linkedin", "twitter", "facebook", "whatsapp", "email"];
    },
  },
  apollo: {
    getSubscriberProfile() {
      return {
        query: require("../../api/query/getSubscriberProfile.gql"),
        result({ data }) {
          if (data?.getSubscriberProfile) {
            this.subscriberInfo = data.getSubscriberProfile;
          }
        },
      };
    },
  },
  methods: {
    qrCodeSvg(size, isSvgViewBox = false) {
      return new QrCodeHelper({
        content: this.shareUrl,
        padding: 0,
        width: size,
        height: size,
        color: "#281166",
        container: isSvgViewBox ? "svg-viewbox" : "svg",
      }).svg();
    },
    async copyToClipBoard(text) {
      this.copyingShareUrl = true;

      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        const tmpTextField = document.createElement("textarea");
        tmpTextField.textContent = text;
        tmpTextField.setAttribute("style", "position: absolute; right: 200%;");
        document.body.appendChild(tmpTextField);
        tmpTextField.select();
        tmpTextField.setSelectionRange(0, 99999); /*For mobile devices*/
        document.execCommand("copy");
        tmpTextField.remove();
      }

      this.copyingShareUrl = false;
    },
    async downloadQrCode() {
      if (this.downloadQrCodeLoading) return;

      this.downloadQrCodeLoading = true;

      await this.downloadHtmlAsImage({
        dataList: [
          {
            htmlElement: this.$refs.qrCodeSvg,
            fileName: `my-qr-code-${Date.now()}`,
          },
        ],
      });

      this.downloadQrCodeLoading = false;
    },
    async downloadProfileCardAsPdf() {
      if (this.downloadProfileLoading) return;
      this.downloadProfileLoading = true;

      await bulkDownloadProfileCards({
        employeeProfiles: [
          {
            ...this.userProfile.employee,
            user: this.userProfile,
          },
        ],
        subscriberInfo: this.subscriberInfo,
        image: true,
      });

      this.downloadProfileLoading = false;
    },
    async downloadCertificatesAsPdf() {
      if (this.downloadCertificatesLoading) return;
      this.downloadCertificatesLoading = true;

      await bulkDownloadTrainingCertificates({
        trainingCertificates:
          this.userProfile?.employee?.trainingCertificates?.map(x => ({
            ...x,
            //to get employee user avatar
            employee: {
              user: {
                ...this.userProfile,
              },
            },
          })) ?? [],
        subscriberInfo: this.subscriberInfo,
      });

      this.downloadCertificatesLoading = false;
    },
  },
};
</script>
